import envelope from '@turf/envelope'
import {feature, featureCollection} from '@turf/helpers'
import {Extent} from 'ol/extent'
import {GeoJSON} from 'ol/format'
import React, {useEffect, useMemo, useState} from 'react'
import {selectParcel} from '../../common/slice'
import {useAppDispatch, useAppSelector} from '../../common/store'
import {replaceSpaceWithX, useQuery} from '../../common/util'
import {
  AERIAL_PHOTO_SERVICES_EMAIL,
  AP_POINT_RADIUS_MAX,
  LAYER_AERIAL_PHOTOGRAPHY_PATH,
  LAYER_MAPMARKER_PRIMARY_COLOUR, MAP_PROJECTION, MAPPROXY_PROJECTION,
} from '../../environment'
import {MapConfigComponentProps} from '../map/MapComponent'
import MapMarkers, {MapMarker} from '../map/MapMarkers'
import VctFooter from '../vct-footer/VctFooter'
import {APDataType, selectParcelByPolygon, updateSearchValue} from './ap.slice'
import {APUtils} from './ap.util'
import {selectCartItems} from './apCart.slice'
import {clearSelectFrame, selectZoomExtentMode} from './apFrames.slice'
import {useApMapData} from './apMapData.hook'
import VectorSource from "ol/source/Vector";
import Vector from "ol/layer/Vector";
import {Draw, Snap} from "ol/interaction";
import {GeoJSONPolygon} from "ol/format/GeoJSON";
import {selectDrawModeState, updateDrawMode} from "./apDrawPoygonSlice";
import {DrawEvent} from "ol/interaction/Draw";
import {unByKey} from "ol/Observable";
import {EventsKey} from "ol/events";
import {Feature} from "ol";
import {useHistory} from "react-router-dom";
import center from "@turf/center";
import Point from "ol/geom/Point";
import {Geometry} from "ol/geom";
import {Polygon} from "geojson";


/**
 * Creates a buffer on the extent, to account for the details/cart panels and other buttons/overlays
 * @param extent
 */
const bufferExtent = (extent: Extent) => {
  const bufferAmount = 0.25
  const extentWidth = (extent[2] - extent[0])
  const extentHeight = (extent[3] - extent[1])
  // Apply a percentage buffer around each side
  extent[0] -= extentWidth * bufferAmount
  extent[1] -= extentHeight * bufferAmount
  extent[2] += extentWidth * bufferAmount
  extent[3] += extentHeight * bufferAmount
  // Because the panel takes up roughly 33% of the frame, calculate the maximum width of the extent,
  // and subtract another half from the left side
  extent[0] -= extentWidth / 2
  return extent
}

export const APMapConfig = ({
                              map,
                              zoomTo,
                              zoomToExtent,
                              setReturnLocation,
                              onSingleClick,
                            }: MapConfigComponentProps) => {
  const zoomExtentMode = useAppSelector(selectZoomExtentMode)
  const cartItems = useAppSelector(selectCartItems)
  const drawMode = useAppSelector(selectDrawModeState)
  const { layers, visibleFrames, selectedFrame, snap, draw } = useApMapData()
  const primaryMarker = APUtils.useAPData()
  const dispatch = useAppDispatch()
  const query = useQuery()
  const history = useHistory()
  const geoJson = useMemo(() => new GeoJSON(), [])
  const [eventKey, setEventKey ] = useState({} as EventsKey)

  if (map) {
    map.setLayers(layers)
  }

  // Calculate the bounds of the selected frame(s) for map zooming
  useEffect(() => {
    if (map && selectedFrame) {
      const extent = (geoJson.readGeometry(selectedFrame.geometry).transform('EPSG:4326', 'EPSG:3857')).getExtent()

      if (zoomToExtent && extent) {
        zoomToExtent(bufferExtent(extent))
      }
    }
  }, [map, geoJson, selectedFrame, zoomToExtent])


  useEffect(() => {
    if (map && zoomExtentMode === 'visibleFrames') {
      if (visibleFrames && visibleFrames.length > 0) {
        const features = featureCollection(visibleFrames.filter(value => !!value.geometry).map(value => feature(value.geometry!)))
        const visibleFeaturesEnvelope = envelope(features)
        const extent = (geoJson.readGeometry(visibleFeaturesEnvelope.geometry).transform('EPSG:4326', 'EPSG:3857')).getExtent()

        if (zoomToExtent && extent) {
          zoomToExtent(bufferExtent(extent))
        }
      }
    }
  }, [map, geoJson, visibleFrames, zoomExtentMode, zoomToExtent])

  useEffect(() => {
    if (map && zoomExtentMode === 'cartFrames') {
      if (cartItems && cartItems.length > 0) {
        const features = featureCollection(cartItems.map(value => value.frame).filter(value => !!value.geometry).map(value => feature(value.geometry!)))
        const visibleFeaturesEnvelope = envelope(features)
        const extent = (geoJson.readGeometry(visibleFeaturesEnvelope.geometry).transform('EPSG:4326', 'EPSG:3857')).getExtent()

        if (zoomToExtent && extent) {
          zoomToExtent(bufferExtent(extent))
        }
      }
    }
  }, [map, geoJson, cartItems, zoomExtentMode, zoomToExtent])

  useEffect(
    () => {
      if (primaryMarker) {
        if (query.get('zoomto') !== 'false') {
        }
        setReturnLocation({ 'longitude': primaryMarker.longitude, 'latitude': primaryMarker.latitude })
        dispatch(selectParcel(replaceSpaceWithX(primaryMarker.laisKey)))
      }
    },
    [primaryMarker, setReturnLocation, dispatch, query],
  )

  // ************************************************************************************************************** //
  // Additions to mark out a polygon on the map
  // ************************************************************************************************************** //

  function addInteractions() {
    let key = draw.on('drawend', handleEndOfDrawEvent)
    setEventKey(key)

    if (map)
    {
      map.addInteraction(draw)
      map.addInteraction(snap)
    }
  }

  function removeInteractions() {
    if (map)
    {
      if (draw) { map.removeInteraction(draw) }
      if (snap) { map.removeInteraction(snap) }
    }
    if (eventKey) { unByKey(eventKey) }
  }

  useEffect( () => {
    if (map && drawMode) { addInteractions() }

    return () => {
      if (map && drawMode) { removeInteractions() }
    }
  }, [map, drawMode])

  function handleEndOfDrawEvent (e: DrawEvent) {
    const polygon = new GeoJSON().writeGeometryObject(e.feature.getGeometry()!.transform(MAP_PROJECTION, MAPPROXY_PROJECTION)) as GeoJSONPolygon;

    // This is the only time we touch the URL, and that is to set the new path that we'll be using
    // for searching with. The selected frames are added to the URL by the APDataTable component.
    updateUrlWithCoordinatesOfPoint(polygon)

    dispatch(clearSelectFrame())
    dispatch(updateSearchValue(''))

    sendPolygonDetailsToService(e.feature.getGeometry()!)
    updateMapWithDrawnPolygonDetails(polygon)
  }

  function updateUrlWithCoordinatesOfPoint (polygon: Polygon) {
    const point = new Point(center (polygon).geometry.coordinates)
    let [ longitude, latitude ] = point.getCoordinates()

    //@ts-ignore this is set by envNumber with a default value if not defined
    let url = APUtils.buildCoordinateSearchPath(longitude, latitude, AP_POINT_RADIUS_MAX)

    history.replace(url)
  }

  function sendPolygonDetailsToService (geometry: Geometry) {
    let polygon = new GeoJSON().writeGeometryObject(geometry.transform(MAPPROXY_PROJECTION, MAP_PROJECTION )) as GeoJSONPolygon;
    dispatch(selectParcelByPolygon({geometry: polygon}))
  }

  function updateMapWithDrawnPolygonDetails (polygon: Polygon) {
    dispatch(updateDrawMode({active: false, geometry: polygon}))
  }

  return (
    <>
      {primaryMarker &&
          <MapMarkers map={map}>
              <MapMarker<APDataType>
                  latitude={primaryMarker.latitude}
                  longitude={primaryMarker.longitude}
                  data={primaryMarker}
                  iconColor={LAYER_MAPMARKER_PRIMARY_COLOUR}
                  iconSize={'big'}/>
          </MapMarkers>
      }
      <VctFooter contactEmail={AERIAL_PHOTO_SERVICES_EMAIL}/>
    </>
  )
}
